<script lang="ts" setup>
import { iconMoreHorizontalSmall, iconX } from "@sit-onyx/icons";
import { computed } from "vue";
import { useDensity } from "../../composables/density.js";
import {
  SKELETON_INJECTED_SYMBOL,
  useSkeletonContext,
} from "../../composables/useSkeletonState.js";
import { useVModel } from "../../composables/useVModel.js";
import { mergeVueProps } from "../../utils/attrs.js";
import { useForwardProps } from "../../utils/props.js";
import OnyxFABButton from "../OnyxFABButton/OnyxFABButton.vue";
import OnyxFlyoutMenu from "../OnyxNavBar/modules/OnyxFlyoutMenu/OnyxFlyoutMenu.vue";
import type { OnyxFABProps } from "./types.js";

const props = withDefaults(defineProps<OnyxFABProps>(), {
  alignment: "right",
  skeleton: SKELETON_INJECTED_SYMBOL,
  open: undefined,
});

const emit = defineEmits<{
  /**
   * Emitted when the isExpanded state changes.
   */
  "update:open": [value: boolean];
}>();

const slots = defineSlots<{
  /**
   * Nested options to show inside a flyout.
   */
  default?(): unknown;
}>();

const { densityClass } = useDensity(props);
const skeleton = useSkeletonContext(props);
const fabButtonProps = useForwardProps(props, OnyxFABButton);

/**
 * If the flyout is expanded or not.
 */
const isExpanded = useVModel({
  props,
  emit,
  key: "open",
  default: false,
});

const hasOptions = computed(() => !!slots.default);

const triggerIcon = computed(() => {
  if (!hasOptions.value) return props.icon;
  return isExpanded.value ? iconX : iconMoreHorizontalSmall;
});
</script>

<template>
  <OnyxFABButton
    v-if="!hasOptions || skeleton"
    :class="['onyx-fab', `onyx-fab--${props.alignment}`]"
    v-bind="fabButtonProps"
    :icon="triggerIcon"
    :skeleton
  />

  <OnyxFlyoutMenu
    v-else
    v-model:open="isExpanded"
    :label="props.label"
    trigger="click"
    :class="['onyx-fab', `onyx-fab--${props.alignment}`, densityClass]"
    :alignment="props.alignment"
  >
    <template #button="{ trigger }">
      <OnyxFABButton
        v-bind="mergeVueProps(props, trigger)"
        :label="props.label"
        hide-label
        :icon="triggerIcon"
      />
    </template>

    <template #options>
      <slot name="default"></slot>
    </template>
  </OnyxFlyoutMenu>
</template>

<style lang="scss">
@use "../../styles/mixins/layers.scss";

.onyx-fab {
  @include layers.component() {
    --onyx-fab-viewport-gap: var(--onyx-density-sm);
    --onyx-fab-offset-x: 0rem;
    --onyx-fab-offset-y: 0rem;
    --onyx-basic-popover-gap: var(--onyx-density-sm);

    font-family: var(--onyx-font-family-paragraph);
    color: var(--onyx-color-text-icons-neutral-inverted);
    position: fixed;
    bottom: calc(var(--onyx-fab-viewport-gap) + var(--onyx-fab-offset-y));
    right: calc(var(--onyx-fab-viewport-gap) + var(--onyx-fab-offset-x));
    z-index: var(--onyx-z-index-notification);

    .onyx-flyout-menu__list-header,
    .onyx-flyout-menu__list-footer {
      display: none;
    }

    .onyx-flyout-menu__wrapper {
      display: flex;
      flex-direction: column;
      gap: var(--onyx-density-2xs);
      align-items: flex-end;
    }

    &--left {
      right: unset;
      left: calc(var(--onyx-fab-viewport-gap) + var(--onyx-fab-offset-x));

      .onyx-flyout-menu__wrapper {
        align-items: flex-start;
      }
    }

    .onyx-basic-popover__dialog {
      --onyx-basic-popover-min-width: 0;
      box-shadow: none;
      background-color: transparent;
      outline: none;
      width: calc(1.5rem + 2 * var(--onyx-density-md)); // density-md = fab button padding

      &:not(:has(.onyx-fab-button__label)) {
        .onyx-flyout-menu__wrapper {
          align-items: center;
        }
      }
    }

    .onyx-basic-popover__dialog,
    .onyx-flyout-menu__wrapper {
      // prevent cutting of the options outline and box shadow
      // since the floating action button is always positioned at the bottom corner and should
      // not have too many options, we assume that not scrolling when overflowing is acceptable.
      overflow: visible;
    }
  }
}
</style>
